import { useState, useRef, useEffect } from 'react';
import { ArrowsOutCardinal } from '@phosphor-icons/react';

/**
 * Lightweight focal-point picker for tournament card thumbnails.
 *
 * The uploaded image is displayed inside a 16/9 preview frame (matching the
 * tournament card aspect). Organizers drag the image to choose WHICH part of it
 * should be centered — no cropping or re-uploading. We store just two numbers
 * (`focusX`, `focusY`, both 0–100) which the card uses as CSS `object-position`.
 *
 * Props:
 *   src: image URL to reposition
 *   focusX / focusY: initial focus percentages (default 50/50 = center)
 *   onChange: (fx, fy) => void, called on every drag update + slider change
 */
const CARD_ASPECT = 16 / 9;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const BannerCropAdjuster = ({ src, focusX = 50, focusY = 50, onChange }) => {
  const [fx, setFx] = useState(clamp(Number(focusX) || 50, 0, 100));
  const [fy, setFy] = useState(clamp(Number(focusY) || 50, 0, 100));
  const dragState = useRef(null); // { startX, startY, startFx, startFy }
  const frameRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    setFx(clamp(Number(focusX) || 50, 0, 100));
    setFy(clamp(Number(focusY) || 50, 0, 100));
  }, [src, focusX, focusY]);

  const onPointerDown = (e) => {
    const frame = frameRef.current;
    const img = imgRef.current;
    if (!frame || !img) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startFx: fx,
      startFy: fy,
    };
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const frame = frameRef.current;
    const img = imgRef.current;
    if (!frame || !img) return;

    const frameRect = frame.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    // Overflow of the (object-cover) image beyond the frame in each axis.
    const overflowX = Math.max(0, imgRect.width - frameRect.width);
    const overflowY = Math.max(0, imgRect.height - frameRect.height);

    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;

    // Drag semantics: dragging RIGHT should reveal more of the LEFT of the image,
    // so focus % decreases as dx increases. Scale by the overflow in that axis.
    const nextFx = overflowX > 0
      ? clamp(dragState.current.startFx - (dx / overflowX) * 100, 0, 100)
      : 50;
    const nextFy = overflowY > 0
      ? clamp(dragState.current.startFy - (dy / overflowY) * 100, 0, 100)
      : 50;

    const rx = Math.round(nextFx);
    const ry = Math.round(nextFy);
    setFx(rx);
    setFy(ry);
    onChange && onChange(rx, ry);
  };

  const onPointerUp = (e) => {
    dragState.current = null;
    try { e.currentTarget.releasePointerCapture && e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const setSlider = (axis) => (e) => {
    const v = Number(e.target.value);
    if (axis === 'x') { setFx(v); onChange && onChange(v, fy); }
    else { setFy(v); onChange && onChange(fx, v); }
  };

  const reset = () => { setFx(50); setFy(50); onChange && onChange(50, 50); };

  if (!src) return null;

  return (
    <div className="mt-3 space-y-3" data-testid="banner-crop-adjuster">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Adjust Thumbnail</p>
        <button
          type="button"
          onClick={reset}
          className="text-xs font-semibold text-gray-500 hover:text-[#F97316]"
          data-testid="banner-crop-reset-btn"
        >
          Reset to center
        </button>
      </div>

      {/* Card-aspect draggable preview (matches how tournament cards actually render). */}
      <div
        ref={frameRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="relative w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-900 select-none cursor-grab active:cursor-grabbing touch-none"
        style={{ aspectRatio: `${CARD_ASPECT}` }}
        data-testid="banner-crop-frame"
      >
        <img
          ref={imgRef}
          src={src}
          alt="Adjust thumbnail"
          draggable={false}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{
            objectFit: 'cover',
            objectPosition: `${fx}% ${fy}%`
          }}
          data-testid="banner-crop-preview"
        />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur text-white text-[11px] px-2.5 py-1 rounded-full pointer-events-none">
          <ArrowsOutCardinal size={12} weight="bold" /> Drag to reposition
        </div>
        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white text-[11px] px-2.5 py-1 rounded-full font-mono pointer-events-none">
          {fx}% · {fy}%
        </div>
      </div>

      {/* Slider fallback (accessibility + fine control). */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Horizontal</label>
          <input
            type="range" min="0" max="100" value={fx}
            onChange={setSlider('x')}
            className="w-full accent-[#F97316]"
            data-testid="banner-crop-slider-x"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Vertical</label>
          <input
            type="range" min="0" max="100" value={fy}
            onChange={setSlider('y')}
            className="w-full accent-[#F97316]"
            data-testid="banner-crop-slider-y"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500">
        This is exactly how your tournament card will appear to players. The full poster stays visible on the tournament details page.
      </p>
    </div>
  );
};

export default BannerCropAdjuster;
